/**
 * The thinking-level vocabulary — the ONE runtime source, in a module that costs
 * a browser bundle nothing.
 *
 * ⚖ Split out of `index.ts` 2026-08-26 at `portal`'s ask (option D). The list
 * itself was already the single source of truth; what was missing was a way to
 * reach it from a BROWSER without dragging the facade barrel along. The barrel
 * value-exports `getEnvApiKey`/`findEnvKeys`/`calculateCost`, which pull a
 * node-flavoured LLM gateway (`process.env` reads) into whatever bundles it —
 * so `lexportal`'s `ProfilePicker` was left hand-writing a five-member union
 * while resolver, storage and lexserver all accepted six. Consuming this
 * subpath is the fix; a local copy is not (see the ⚠ below).
 *
 * 🔒 INVARIANT — THIS MODULE HAS NO RUNTIME IMPORTS, AND THAT IS ITS WHOLE
 * POINT. The upstream reference below is `import type`, erased at compile time,
 * so the emitted `thinking.js` is a bare array. Adding ANY value import here
 * silently re-poisons every SPA that consumes `@lexiun/lx-llm-core/thinking` —
 * the bundle grows and nothing fails. `tests/facade.test.ts` asserts the
 * absence against this file's source; if you need a value from elsewhere, it
 * belongs in `index.ts`, not here.
 *
 * ⚠ The guard below is bidirectional ON PURPOSE. A one-way check
 * (`satisfies readonly ThinkingLevel[]`) proves only that each member IS a
 * level — it accepts a SUBSET, which is exactly how a five-member constant sat
 * for months under a comment claiming it was type-synced. `Missing` forces the
 * OTHER direction: if upstream adds a level and this list does not, `Missing`
 * stops being `never` and the assignment fails to compile, pointing here.
 *
 * ⚠ Consumers must BUILD from this list — `z.enum(THINKING_LEVELS)`, a `.map()`
 * over it for a dropdown — never retype it. That is not hypothetical:
 * `lexserver` found the list restated **eight times** in their package (three
 * still at five members after upstream grew a sixth), `harn-sdk`'s manifest
 * schema carried its own five-member copy, and `lexportal` carried a ninth.
 */
/** The thinking levels as a RUNTIME list — the value form of `ThinkingLevel`. */
export const THINKING_LEVELS = [
    "minimal",
    "low",
    "medium",
    "high",
    "xhigh",
    "max",
];
// Both must be `never`; either direction breaking is a compile error HERE,
// beside the list, rather than a silent divergence at some consumer.
const _thinkingLevelsAreExhaustive = true;
void _thinkingLevelsAreExhaustive;
//# sourceMappingURL=thinking.js.map